// @ts-nocheck
/*
 * Port of views/dashboard/agent-break-exceeded/agent_break_exceeded_list.js.
 * API: API_AGENT_BREAK_EXCEEDED_STATS?scope=today -> today's (IST) latest 5
 * ended breaks + today's total / exceeded counts for the pie.
 * Date filter removed (2026-10-30): data is always today. "View more" opens
 * the Agent Breaks Records page (full list, date range, server paging).
 * Columns + row mapping shared with that page.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, RefreshCw } from 'lucide-react';
import Chart from 'react-apexcharts';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_BREAK_EXCEEDED_STATS } from '@/service/url';
import { ROUTES } from '@/utilities/constant';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { WARNING_COLOR, PRIMARY_COLOR } from '../chart-palette';
import {
  breakRecordColumns,
  normalizeBreakStats,
  toBreakRow,
} from '@/features/breaks/AgentBreakRecordsPage';

export default function AgentBreakExceededList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ rows: [], total: 0, exceeded: 0 });

  const load = useCallback(() => {
    let isMounted = true;
    setLoading(true);
    getApiCall(`${API_AGENT_BREAK_EXCEEDED_STATS}?scope=today`)
      .then((resp) => { if (isMounted) setData(normalizeBreakStats(resp)); })
      .catch(() => { if (isMounted) setData({ rows: [], total: 0, exceeded: 0 }); })
      .finally(() => { if (isMounted) setLoading(false); });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => load(), [load]);

  const tableRows = useMemo(() => data.rows.map(toBreakRow), [data.rows]);

  const pieOptions = {
    chart: { type: 'pie', toolbar: { show: false } },
    labels: ['Exceeded Breaks', 'Breaks Taken'],
    legend: { position: 'bottom' },
    dataLabels: { enabled: true },
    stroke: { width: 1 },
    colors: [WARNING_COLOR, PRIMARY_COLOR],
  };
  const pieSeries = [data.exceeded, Math.max(0, data.total - data.exceeded)];

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold text-foreground">
          <BarChart3 className="h-4 w-4 text-primary" />
          Agent Break Stats
          <span className="rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">Today</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(`/${ROUTES.BREAKS.RECORDS}`)}>
            View more
          </Button>
          <button
            type="button"
            onClick={load}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="Refresh Agent Break Stats"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {data.exceeded > 0 && (
        <div className="mb-4 shadow-neu-raised rounded-lg bg-card p-4 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
          <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-[1fr_2fr]">
            <div><Chart options={pieOptions} series={pieSeries} type="pie" height={280} /></div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <div className="text-xs text-muted-foreground">Total Exceeded Breaks</div>
                <div className="text-3xl font-bold" style={{ color: '#ef4444' }}>{data.exceeded}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total Breaks Taken</div>
                <div className="text-3xl font-bold" style={{ color: '#3b82f6' }}>{data.total}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <DataTable
        tableId="dashboard.agent-break-exceeded"
        columns={breakRecordColumns}
        data={tableRows}
        isLoading={loading}
        disableSearch
        emptyTitle="No breaks today"
        emptyDescription="No ended agent breaks today."
        pageSize={5}
        exportFilename="agent-break-exceeded-today.csv"
      />
    </>
  );
}
